import { useState, useEffect, useRef } from 'react';
import { STEPS, EXPORT_CHANNELS } from './PresentationBannerConstants';
import { GeneratePanel }  from './PresentationGeneratePanel';
import { StylePanel }     from './PresentationStylePanel';
import { PolishPanel }    from './PresentationPolishPanel';
import { SharePanel }     from './PresentationSharePanel';

interface Props { onSignUp: () => void; }

const SHARE_STATS = [
  { label: 'PowerPoint',    pct: 100, note: 'Full .pptx compat',  color: '#D04000' },
  { label: 'Google Slides', pct: 100, note: 'One-click import',   color: '#4285F4' },
  { label: 'Web Viewer',    pct: 100, note: 'HD branded URL',     color: '#7C3AED' },
  { label: 'Video Export',  pct: 100, note: '1080p MP4',          color: '#059669' },
];

export default function PresentationPublishBanner({ onSignUp }: Props) {
  const [activeStep, setActiveStep] = useState<string | null>(null);
  const [animateIn, setAnimateIn]   = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => { const t = setTimeout(() => setAnimateIn(true), 200); return () => clearTimeout(t); }, []);

  useEffect(() => {
    if (activeStep) setTimeout(() => panelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 50);
  }, [activeStep]);

  const activeStepData = STEPS.find(s => s.id === activeStep);

  return (
    <div className="bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 text-white overflow-hidden">
      {/* Top strip */}
      <div className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-rose-500/20 border border-rose-500/40 rounded-xl flex items-center justify-center">
              <i className="ri-share-line text-rose-400 text-base"></i>
            </div>
            <span className="text-sm font-bold text-white">Share &amp; Export Your Presentation</span>
            <span className="text-xs text-slate-400">— PowerPoint, Google Slides, PDF, and 5 more formats</span>
          </div>
          <button onClick={onSignUp} className="flex items-center gap-2 px-4 py-2 bg-rose-500 hover:bg-rose-400 text-white text-xs font-bold rounded-xl transition-all cursor-pointer whitespace-nowrap">
            <i className="ri-arrow-right-up-line text-sm"></i>Start for Free
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-16">
        {/* Headline */}
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-rose-500/15 border border-rose-500/30 rounded-full mb-5">
            <div className="w-1.5 h-1.5 bg-rose-400 rounded-full animate-pulse"></div>
            <span className="text-xs font-bold text-rose-300 uppercase tracking-wider">4-Step Workflow</span>
          </div>
          <h2 className="text-4xl font-extrabold text-white mb-4 leading-tight">
            Generate. Style. Polish.<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-rose-400 to-fuchsia-400">Share Everywhere.</span>
          </h2>
          <p className="text-slate-400 text-base max-w-xl mx-auto leading-relaxed">
            Click any step below to explore — each one is fully interactive. Configure your deck exactly how you need it, then export to <strong className="text-white">8 formats</strong> in one click.
          </p>
        </div>

        {/* ── Step cards ── */}
        <div className="flex items-stretch justify-center gap-0 mb-6 overflow-x-auto pb-2">
          {STEPS.map((step, idx) => {
            const isActive = activeStep === step.id;
            return (
              <div key={step.id} className="flex items-center">
                <button
                  onClick={() => setActiveStep(prev => prev === step.id ? null : step.id)}
                  className={`group flex flex-col items-center gap-2 px-8 py-5 rounded-2xl border-2 transition-all duration-300 cursor-pointer min-w-[140px] ${isActive ? 'border-white/30 bg-white/10 scale-105' : 'border-white/10 bg-white/5 hover:bg-white/10 hover:border-white/20'}`}
                >
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${step.bg} flex items-center justify-center transition-transform duration-300 ${isActive ? 'scale-110' : 'group-hover:scale-105'}`}>
                    <i className={`${step.icon} text-white text-2xl`}></i>
                  </div>
                  <span className="text-sm font-extrabold text-white whitespace-nowrap">{step.label}</span>
                  <span className="text-xs text-slate-500 text-center whitespace-nowrap">{step.desc}</span>
                  <div className={`flex items-center gap-1 text-xs font-bold transition-all duration-300 ${isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`} style={{ color: step.color }}>
                    <span>{isActive ? 'Close' : 'Try it'}</span>
                    <i className={`text-xs ${isActive ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`}></i>
                  </div>
                </button>
                {idx < STEPS.length - 1 && (
                  <div className="flex items-center flex-shrink-0 mx-1">
                    <div className="w-8 h-px bg-gradient-to-r from-rose-500/60 to-fuchsia-500/30"></div>
                    <i className="ri-arrow-right-s-line text-rose-500/60 text-sm -mx-1"></i>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* ── Panel detail ── */}
        {activeStep && activeStepData && (
          <div ref={panelRef} className="mb-10 rounded-3xl border border-white/15 bg-white/5 backdrop-blur-sm overflow-hidden animate-fade-in">
            {/* Panel header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 flex-shrink-0 bg-white/3">
              <div className="flex items-center gap-3">
                <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${activeStepData.bg} flex items-center justify-center flex-shrink-0`}>
                  <i className={`${activeStepData.icon} text-white text-base`}></i>
                </div>
                <div>
                  <h3 className="text-sm font-extrabold text-white">{activeStepData.label}</h3>
                  <p className="text-xs text-slate-500">{activeStepData.desc}</p>
                </div>
              </div>
              <button onClick={() => setActiveStep(null)} className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center cursor-pointer transition-colors">
                <i className="ri-close-line text-slate-400"></i>
              </button>
            </div>
            <div className="p-6">
              {activeStep === 'generate' && <GeneratePanel onSignUp={onSignUp} />}
              {activeStep === 'style'    && <StylePanel    onSignUp={onSignUp} />}
              {activeStep === 'polish'   && <PolishPanel   onSignUp={onSignUp} />}
              {activeStep === 'share'    && <SharePanel    onSignUp={onSignUp} />}
            </div>
          </div>
        )}

        {/* ── Export channel chips ── */}
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3 mb-12">
          {EXPORT_CHANNELS.map(ch => (
            <button key={ch.name}
              onClick={() => { setActiveStep('share'); setTimeout(() => panelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 80); }}
              className="group relative rounded-2xl p-3 flex flex-col items-center gap-2 transition-all duration-200 cursor-pointer border text-center border-white/10 bg-white/5 hover:bg-white/10 hover:border-white/20 hover:scale-105">
              {ch.badge && (
                <span className="absolute -top-1.5 -right-1.5 px-2 py-0.5 rounded-full text-xs font-extrabold text-white" style={{ backgroundColor: ch.color }}>{ch.badge}</span>
              )}
              <div className="w-10 h-10 rounded-xl flex items-center justify-center border bg-white/8 border-white/12">
                <i className={`${ch.icon} text-xl text-slate-400`}></i>
              </div>
              <div>
                <p className="text-xs font-bold text-slate-300 whitespace-nowrap group-hover:text-white transition-colors">{ch.name}</p>
                <p className="text-xs text-slate-500 whitespace-nowrap">{ch.desc}</p>
              </div>
            </button>
          ))}
        </div>

        {/* ── Two-col info ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
            <div className="flex items-center gap-2 mb-5">
              <i className="ri-slideshow-line text-rose-400 text-base"></i>
              <div>
                <p className="text-sm font-extrabold text-white">Export Compatibility</p>
                <p className="text-xs text-slate-500">100% fidelity across all platforms</p>
              </div>
            </div>
            <div className="space-y-3">
              {SHARE_STATS.map(item => (
                <div key={item.label} className="flex items-center gap-3">
                  <span className="text-xs text-slate-400 w-28 flex-shrink-0">{item.label}</span>
                  <div className="flex-1 h-6 bg-white/5 rounded-lg overflow-hidden">
                    <div className="h-full rounded-lg flex items-center justify-end pr-2 transition-all duration-700" style={{ width: animateIn ? `${item.pct}%` : '0%', backgroundColor: item.color + 'cc' }}>
                      <span className="text-xs font-bold text-white">{item.note}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
            <div className="flex items-center gap-2 mb-5">
              <i className="ri-rocket-line text-fuchsia-400 text-base"></i>
              <div>
                <p className="text-sm font-extrabold text-white">Why Present with openclaw.ai</p>
                <p className="text-xs text-slate-500">Built for professional creators</p>
              </div>
            </div>
            <div className="space-y-3">
              {[
                { icon: 'ri-magic-line',         color: 'text-rose-400',   text: 'AI generates your entire deck from a single prompt' },
                { icon: 'ri-paint-brush-line',   color: 'text-fuchsia-400',text: '200+ premium themes with one-click apply' },
                { icon: 'ri-film-line',          color: 'text-violet-400', text: 'Cinematic slide transitions and animations' },
                { icon: 'ri-team-line',          color: 'text-pink-400',   text: 'Real-time team collaboration on every slide' },
                { icon: 'ri-mic-line',           color: 'text-amber-400',  text: 'AI speaker notes auto-generated for each slide' },
                { icon: 'ri-bar-chart-2-line',   color: 'text-emerald-400',text: 'Track views, engagement, and drop-off analytics' },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-white/8 flex-shrink-0">
                    <i className={`${item.icon} ${item.color} text-sm`}></i>
                  </div>
                  <span className="text-sm text-slate-300">{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── CTA ── */}
        <div className="relative rounded-3xl overflow-hidden border border-rose-500/30">
          <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 60% 50%, rgba(244,63,94,0.18) 0%, rgba(192,38,211,0.08) 50%, transparent 80%)' }} />
          <div className="relative px-10 py-10 flex flex-col md:flex-row items-center justify-between gap-8">
            <div>
              <p className="text-xs font-bold text-rose-400 uppercase tracking-widest mb-2">Start presenting today</p>
              <h3 className="text-2xl font-extrabold text-white mb-2 leading-tight">Your first deck.<br />8 export formats. Zero friction.</h3>
              <p className="text-sm text-slate-400 max-w-sm leading-relaxed">Join 300,000+ presenters already using openclaw.ai. Free plan includes 3 decks, full export, and a shareable live URL.</p>
            </div>
            <div className="flex flex-col items-center gap-5 flex-shrink-0">
              <div className="grid grid-cols-3 gap-4 text-center">
                {[{ val: '8', sub: 'Formats' }, { val: '$0', sub: 'To Start' }, { val: '∞', sub: 'Views' }].map(s => (
                  <div key={s.sub}>
                    <p className="text-2xl font-extrabold text-rose-400">{s.val}</p>
                    <p className="text-xs text-slate-500">{s.sub}</p>
                  </div>
                ))}
              </div>
              <button onClick={onSignUp} className="flex items-center gap-2.5 px-8 py-3.5 bg-gradient-to-r from-rose-500 to-fuchsia-500 text-white font-extrabold text-sm rounded-2xl hover:opacity-90 transition-all cursor-pointer whitespace-nowrap">
                <i className="ri-slideshow-line text-base"></i>Create Presentation Free<i className="ri-arrow-right-line text-sm"></i>
              </button>
              <p className="text-xs text-slate-600">No credit card · Free plan forever · Cancel anytime</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
